import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  exportGlobalAccountStatement,
  getBankingDetails,
  getBankingRequests,
  getPendingRfis,
  getRfis,
  getRfiDetail,
  updateGlobalAccountSettings,
} from '../services/banking';
import {
  getUnreconciledDeposits,
  createDepositReconciliations,
  createManualPaymentReconciliation,
} from '../services/invoicing';
import {
  BankingDetails,
  PendingRfi,
  RfisCountResponse,
  UnreconciledDepositsResponse,
  CreateDepositReconciliationsPayload,
  CreateManualPaymentReconciliationPayload,
} from '../types/banking';

interface UseBankingDetailsParams {
  companyId?: number;
  accountId?: number;
}

interface UseBankingRequestsParams {
  companyId?: number;
}

interface UseUpdateGlobalAccountSettingsParams {
  companyId?: number;
  accountId?: number;
}

interface UseExportGlobalAccountStatementParams {
  companyId?: number;
  accountId?: number;
  fileName?: string;
}

interface UsePendingRfisParams {
  companyId?: number;
  accountId?: number;
}

interface UseRfisParams {
  companyId?: number;
  accountId?: number;
  page?: number;
  pageSize?: number;
  status?: string[];
  rfiType?: string[];
}

interface UseRfiDetailParams {
  companyId?: number;
  accountId?: number;
  id?: number;
}

interface UseUnreconciledDepositsParams {
  companyId?: number;
  accountId?: number;
  startDate?: string;
  currency?: string;
}

interface UseCreateDepositReconciliationsParams {
  companyId?: number;
  accountId?: number;
}

interface UseCreateManualPaymentReconciliationParams {
  companyId?: number;
}

export const BANKING_DETAILS_QUERY_KEY = 'bankingDetails';
export const BANKING_REQUESTS_QUERY_KEY = 'bankingRequests';
export const BANKING_STATEMENT_QUERY_KEY = 'bankingStatement';
export const BANKING_ACCOUNTS_QUERY_KEY = 'bankingAccounts';
export const PENDING_RFIS_QUERY_KEY = 'pendingRfis';
export const RFIS_QUERY_KEY = 'rfis';
export const RFI_DETAIL_QUERY_KEY = 'rfiDetail';
export const UNRECONCILED_DEPOSITS_QUERY_KEY = 'unreconciledDeposits';

// Fetches a single RFI (refreshing only that one from Airwallex), so opening an
// RFI does not wait for the account-wide refresh that usePendingRfis triggers.
export const useRfiDetail = ({ companyId, accountId, id }: UseRfiDetailParams) => {
  return useQuery<PendingRfi>({
    queryKey: [RFI_DETAIL_QUERY_KEY, companyId, accountId, id],
    queryFn: () => getRfiDetail(companyId!, accountId!, id!),
    enabled: !!companyId && !!accountId && !!id,
  });
};

export const usePendingRfis = ({ companyId, accountId }: UsePendingRfisParams) => {
  return useQuery<PendingRfi[]>({
    queryKey: [PENDING_RFIS_QUERY_KEY, companyId, accountId],
    queryFn: () => getPendingRfis(companyId!, accountId!),
    enabled: !!companyId && !!accountId,
  });
};

export const useRfis = ({ companyId, accountId, page, pageSize, status, rfiType }: UseRfisParams) => {
  return useQuery<RfisCountResponse>({
    queryKey: [RFIS_QUERY_KEY, companyId, accountId, page, pageSize, status, rfiType],
    queryFn: () => getRfis(companyId!, accountId!, { page, pageSize, status, rfiType }),
    enabled: !!companyId && !!accountId,
  });
};

export const useBankingDetails = ({ companyId, accountId }: UseBankingDetailsParams) => {
  return useQuery<BankingDetails>({
    queryKey: [BANKING_DETAILS_QUERY_KEY, companyId, accountId],
    queryFn: () => getBankingDetails(companyId!, accountId!),
    enabled: !!companyId && !!accountId,
  });
};

export const useUnreconciledDeposits = (
  { companyId, accountId, startDate, currency }: UseUnreconciledDepositsParams,
  enabled = true
) => {
  return useQuery<UnreconciledDepositsResponse>({
    queryKey: [UNRECONCILED_DEPOSITS_QUERY_KEY, companyId, accountId, startDate, currency],
    queryFn: () =>
      getUnreconciledDeposits(companyId!, accountId!, {
        startDate: startDate!,
        currency: currency!,
      }),
    enabled: enabled && !!companyId && !!accountId && !!startDate && !!currency,
  });
};

export const useCreateDepositReconciliations = ({
  companyId,
  accountId,
}: UseCreateDepositReconciliationsParams) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDepositReconciliationsPayload) =>
      createDepositReconciliations(companyId!, accountId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([UNRECONCILED_DEPOSITS_QUERY_KEY, companyId, accountId]);
    },
  });
};

export const useCreateManualPaymentReconciliation = ({
  companyId,
}: UseCreateManualPaymentReconciliationParams) => {
  return useMutation({
    mutationFn: (payload: CreateManualPaymentReconciliationPayload) =>
      createManualPaymentReconciliation(companyId!, payload),
  });
};

export const useBankingRequests = ({ companyId }: UseBankingRequestsParams) => {
  return useQuery({
    queryKey: [BANKING_REQUESTS_QUERY_KEY, companyId],
    queryFn: () => getBankingRequests(companyId!),
    enabled: !!companyId,
  });
};

export const useUpdateGlobalAccountSettings = ({
  companyId,
  accountId,
}: UseUpdateGlobalAccountSettingsParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: any) => updateGlobalAccountSettings(companyId!, accountId!, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BANKING_DETAILS_QUERY_KEY, companyId, accountId] });
    },
  });
};

export const useExportGlobalAccountStatement = ({
  companyId,
  accountId,
  fileName = 'statement',
}: UseExportGlobalAccountStatementParams) => {
  return useMutation({
    mutationFn: (globalAccountId: string) =>
      exportGlobalAccountStatement(companyId!, accountId!, globalAccountId),
    onSuccess: (data) => {
      const blob = new Blob([data.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    },
  });
};
